import { Location } from '@angular/common';
import {
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EVENTS_PER_PAGE,
  PortalCalendarDateRangeFilter,
  PortalCalendarFilter,
  PortalCalendarSingleDayFilter,
  PortalEventList,
  PortalEventsDateAndSearchFilter,
  PortalEventsFilter,
  PortalEventsService,
  PortalInfiniteEventsFilter,
  PortalMonthCalendarFilter,
  PortalMonthEventsFilter,
  PortalSingleDayEventsFilter,
  PortalSingleEventFilter,
  TixPortalService
} from '@tix/event-buyer/services';
import { TixEventCalendarComponent } from 'libs/event-buyer/components/src/lib/event-calendar/event-calendar.component';
import { BuyerService } from 'libs/event-buyer/components/src/lib/services/buyer.service';
import { Subscription } from 'rxjs';
import { debounceTime, map, startWith, switchMap, tap } from 'rxjs/operators';
import { TixAnalyticsService } from '@tix/shared/analytics';

@Component({
  selector: 'tix-venue-portal',
  templateUrl: './venue-portal.component.html',
  styleUrls: ['./venue-portal.component.scss']
})
export class VenuePortalComponent implements OnInit, OnDestroy, OnChanges {
  subs: Subscription[] = [];
  constructor(
    private router: Router,
    public route: ActivatedRoute,
    private portalService: TixPortalService,
    private portalEventsService: PortalEventsService,
    private buyerService: BuyerService,
    private location: Location,
    private tixAnalytics: TixAnalyticsService
  ) {}

  visitVenueWebsite(url: string) {
    this.tixAnalytics.captureEvent('visit_venue_website', {
      venueId: this.venueId
    });
    window.open(url, '_blank');
  }

  get venueId() {
    return this.route.snapshot.data['venueId'];
  }

  // Handling single event page
  get isSingleEventPage() {
    return !!this.route.snapshot.queryParams['searchEvent'];
  }

  get selectedSingleEventId() {
    return this.route.snapshot.queryParams['searchEvent'] as string;
  }

  // Search handling
  get searchQuery() {
    return this.route.snapshot.queryParams['searchQuery'] ?? '';
  }

  isMobileView() {
    return window.innerWidth < 1024;
  }

  set searchQuery(value: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        searchQuery: value || null
      },
      queryParamsHandling: 'merge'
    });
  }
  searchQueryControl = new FormControl('');

  @ViewChild(TixEventCalendarComponent)
  calendarComp?: TixEventCalendarComponent;

  listenToChangeForSearchQuery() {
    this.subs.push(
      this.searchQueryControl.valueChanges
        .pipe(
          debounceTime(250),
          tap(searchQuery => {
            if (this.filter) {
              this.updateFilter(
                this.filter.withSearch(searchQuery, this.eventsSlugKeyword)
              );
            } else {
              this.updateFilter(
                new PortalInfiniteEventsFilter(
                  searchQuery,
                  1,
                  this.eventsSlugKeyword
                )
              );
            }

            this.searchQuery = searchQuery;
          })
        )
        .subscribe(() => {})
    );
  }

  get eventsSlugKeyword(): string {
    const firstRouteChild = this.route.firstChild;
    if (!firstRouteChild) return '';
    return firstRouteChild.snapshot.paramMap.get('event-keyword') ?? '';
  }

  // Handle calendar date filters
  get selectedCalendarDateFilter(): PortalCalendarFilter | null {
    const startDate = this.route.snapshot.queryParams['filterStartDate'];
    const endDate = this.route.snapshot.queryParams['filterEndDate'];

    const y = parseInt(this.route.snapshot.queryParams['year'] ?? -1);
    const m = parseInt(this.route.snapshot.queryParams['month'] ?? -1);
    const d = parseInt(this.route.snapshot.queryParams['date'] ?? -1);

    if (startDate && endDate) {
      return new PortalCalendarDateRangeFilter(
        new Date(startDate),
        new Date(endDate)
      );
    }

    if (y === -1 && m === -1 && d === -1) return null;
    if (y !== -1 && m !== -1 && d === -1)
      return new PortalMonthCalendarFilter(y, m);
    if (y !== -1 && m !== -1 && d !== -1)
      return new PortalCalendarSingleDayFilter(y, m, d);
    return null;
  }

  set selectedCalendarDateFilter(value: PortalCalendarFilter | null) {
    // Having them initially set as null will remove them from the query params
    // if they don't get assigned a value
    let year = null;
    let month = null;
    let date = null;
    let filterStartDate = null;
    let filterEndDate = null;

    if (value instanceof PortalCalendarDateRangeFilter) {
      filterStartDate = value.startDate;
      filterEndDate = value.endDate;
    }

    if (value instanceof PortalCalendarSingleDayFilter) {
      year = value.year;
      month = value.month;
      date = value.day;
    } else if (value instanceof PortalMonthCalendarFilter) {
      year = value.year;
      month = value.month;
    }

    this.router.navigate([], {
      queryParams: { year, month, date, filterStartDate, filterEndDate },
      queryParamsHandling: 'merge'
    });
  }

  // Get venue data
  venue$ = this.portalService.getVenueInfo(this.venueId);

  // Keeping track of the loaded events
  events: PortalEventList = [];

  isLoadingFirstPageEvents = false;
  isLoadingNextPageEvents = false;

  hasReachedTheEnd = false;

  filter: PortalEventsFilter;

  // This is added to fix the issue with 'this' inside handleScroll
  scrollHandler: (event: Event) => void;

  ngOnInit(): void {
    console.log('ttete', this.venueId, this.eventsSlugKeyword);
    this.buyerService.setCanClearFilters(true);
    if (this.searchQuery) {
      this.searchQueryControl.setValue(this.searchQuery);
      this.tixAnalytics.captureEvent('search', {
        venueId: this.venueId,
        query: this.searchQuery
      });
    }

    if (this.selectedCalendarDateFilter) {
      this.updateCalendarFilter(this.selectedCalendarDateFilter);
    } else if (this.isSingleEventPage) {
      this.updateFilter(
        new PortalSingleEventFilter(this.selectedSingleEventId)
      );
    } else {
      this.buyerService.setCanClearFilters(false);
      this.updateFilter(
        new PortalInfiniteEventsFilter(
          this.searchQuery,
          1,
          this.eventsSlugKeyword
        )
      );
    }

    this.scrollHandler = (event: Event) => this.handleScroll(event);
    window.addEventListener('scroll', this.scrollHandler);

    this.listenToChangeForSearchQuery();

    this.buyerService.setClearFiltersListener(() => {
      this.tixAnalytics.captureEvent('clear_all_filters', {
        venueId: this.venueId
      });
      this.clearFiltersAndReload();
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.scrollHandler);
    this.subs.forEach(sub => sub.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {}

  trackButtonClick(eventName: string) {
    if (eventName === 'Calendar View') {
      this.tixAnalytics.captureEvent('switch_to_calendar_view', {
        venueId: this.venueId
      });
    }
  }

  // Handle "Clear Filters" to reset to original filter
  clearFiltersAndReload() {
    this.router
      .navigate([], {
        queryParams: {}
      })
      .then(() => window.location.reload());
  }

  // Creates a portal event filter from calendar filter
  async updateCalendarFilter(f: PortalCalendarFilter) {
    this.isLoadingFirstPageEvents = true;
    this.selectedCalendarDateFilter = f;
    this.buyerService.setCanClearFilters(true);

    if (f instanceof PortalCalendarSingleDayFilter) {
      this.updateFilter(
        new PortalSingleDayEventsFilter(
          f.year,
          f.month,
          f.day,
          this.searchQuery,
          1,
          this.eventsSlugKeyword
        )
      );
    } else if (f instanceof PortalMonthCalendarFilter) {
      this.updateFilter(
        new PortalMonthEventsFilter(
          f.year,
          f.month,
          this.searchQuery,
          1,
          this.eventsSlugKeyword
        )
      );
    } else if (f instanceof PortalCalendarDateRangeFilter) {
      this.updateFilter(
        new PortalEventsDateAndSearchFilter(
          this.searchQuery,
          1,
          this.eventsSlugKeyword,
          f.startDate,
          f.endDate
        )
      );
    }
  }

  // Fetches the portal events for the selected filter
  async updateFilter(f: PortalEventsFilter) {
    this.filter = f;

    if (f instanceof PortalSingleEventFilter) {
      this.isLoadingFirstPageEvents = true;
    } else {
      let filter = f as PortalEventsDateAndSearchFilter;
      if (filter.pageNumber === 1) this.isLoadingFirstPageEvents = true;
      else this.isLoadingNextPageEvents = true;
    }

    const events = await this.portalEventsService.getEventsForVenueWithFilter(
      this.venueId,
      f
    );
    if (this.isLoadingFirstPageEvents) this.events = events;
    else this.events = [...this.events, ...events];

    this.isLoadingFirstPageEvents = false;
    this.isLoadingNextPageEvents = false;
    this.hasReachedTheEnd = events.length < EVENTS_PER_PAGE;
  }

  fetchNextPage() {
    if (
      this.filter instanceof PortalSingleEventFilter ||
      this.isSingleEventPage ||
      this.hasReachedTheEnd ||
      this.isLoadingFirstPageEvents ||
      this.isLoadingNextPageEvents ||
      !this.events.length
    )
      return;

    console.log('Fetching the next page');
    this.updateFilter(
      PortalEventsDateAndSearchFilter.nextPageFrom(
        this.filter as PortalEventsDateAndSearchFilter
      )
    );
  }

  // Detect scroll to the bottom of the page
  handleScroll(event: Event) {
    if (window.scrollY + 1200 >= document.body.scrollHeight) {
      this.fetchNextPage();
    }
  }
}
